/**
 * Outreach attribution — capture ?pid= from outreach email links and mark
 * conversion when the recipient signs up with the same email.
 */
import { supabase } from '../lib/supabase';

const PID_KEY = 'outreachPersonId';
const REF_OUTREACH = 'outreach';

function readStoredPid(): string | null {
  try {
    const v = window.localStorage.getItem(PID_KEY);
    return v && v.trim().length > 0 ? v.trim() : null;
  } catch {
    return null;
  }
}

function writeStoredPid(pid: string): void {
  try { window.localStorage.setItem(PID_KEY, pid); } catch { /* noop */ }
}

function clearStoredPid(): void {
  try { window.localStorage.removeItem(PID_KEY); } catch { /* noop */ }
}

/** Capture ?ref=outreach&pid=UUID from signup URL (keeps pid after ref is stripped). */
export function captureOutreachPersonIdFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    const pid = params.get('pid')?.trim();

    if (pid && (ref === REF_OUTREACH || (ref === null && window.location.pathname === '/signup'))) {
      writeStoredPid(pid);

      if (params.has('pid')) {
        params.delete('pid');
        const remaining = params.toString();
        const newUrl = window.location.pathname
          + (remaining ? `?${remaining}` : '')
          + window.location.hash;
        try { window.history.replaceState({}, '', newUrl); } catch { /* noop */ }
      }

      return pid;
    }
    return readStoredPid();
  } catch {
    return readStoredPid();
  }
}

const NEW_USER_WINDOW_MS = 10 * 60 * 1000;

export async function attributeOutreachSignupIfPending(): Promise<boolean> {
  const pid = readStoredPid();
  if (!pid) return false;

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return false;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user?.created_at) {
      clearStoredPid();
      return false;
    }

    const ageMs = Date.now() - new Date(user.created_at).getTime();
    if (ageMs > NEW_USER_WINDOW_MS) {
      clearStoredPid();
      return false;
    }

    const { data, error } = await supabase.rpc('outreach_attribute_signup', {
      p_person_id: pid,
    });

    if (error) {
      console.warn('[outreach] attribute signup error:', error.message);
      clearStoredPid();
      return false;
    }

    clearStoredPid();
    return data === true;
  } catch (err) {
    console.warn('[outreach] attributeOutreachSignupIfPending exception:', err);
    clearStoredPid();
    return false;
  }
}
